import React, { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import omit from 'lodash/omit';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Row,
  Typography,
  message,
} from 'antd';

import { GET_ENGAGEMENT_NOTIFICATION_SETTING } from 'api/engagementNotifications/queries';
import { SET_ENGAGEMENT_NOTIFICATION_SETTING } from 'api/engagementNotifications/mutations';
import { GetEngagementNotificationSetting } from 'api/engagementNotifications/types/GetEngagementNotificationSetting';
import {
  SetEngagementNotificationSetting,
  SetEngagementNotificationSettingVariables,
} from 'api/engagementNotifications/types/SetEngagementNotificationSetting';
import { describeWaits, FormValues, notShorterThan } from './helpers';

const { Title, Paragraph, Text } = Typography;

const EngagementNotificationSettings: React.FC = () => {
  const [form] = Form.useForm<FormValues>();

  const { data, loading, error } = useQuery<GetEngagementNotificationSetting>(
    GET_ENGAGEMENT_NOTIFICATION_SETTING,
    { fetchPolicy: 'network-only' }
  );

  const [setSetting, { loading: saving }] = useMutation<
    SetEngagementNotificationSetting,
    SetEngagementNotificationSettingVariables
  >(SET_ENGAGEMENT_NOTIFICATION_SETTING);

  useEffect(() => {
    const setting = data?.getEngagementNotificationSetting;
    if (!setting) return;

    form.setFieldsValue(omit(setting, '__typename'));
  }, [data, form]);

  const handleSave = async (values: FormValues) => {
    try {
      await setSetting({ variables: { input: values } });
      message.success('Notification settings saved.');
    } catch (error) {
      message.error(
        `Could not save settings${error?.message ? ` (${error.message})` : ''}.`
      );
    }
  };

  return (
    <Card loading={loading}>
      <Title level={4}>Like &amp; Comment Notifications</Title>
      <Paragraph type="secondary">
        When people like, comment on or reply on a post, we don&apos;t send one
        notification per action. We collect them per post and send one
        notification like &quot;Ana and 4 others liked your video&quot;. The
        first one goes out fast; while the post keeps getting activity we wait
        longer before the next one, so a busy post never floods anyone.
      </Paragraph>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Could not load the current settings"
          description="Saving is turned off so the real values are not overwritten by mistake. Reload the page to try again."
          style={{ marginBottom: 24 }}
        />
      )}

      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={handleSave}
        requiredMark={false}
      >
        <Divider orientation="left">
          Waiting between notifications for one post
        </Divider>
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Form.Item
              label="1st notification after (min)"
              name="firstWindowMinutes"
              tooltip="How long we wait after the first like or comment on a post. Short = feels instant. During this wait more likes are collected into the same notification."
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                min={1}
                max={1440}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="2nd notification after (min)"
              name="secondWindowMinutes"
              dependencies={['firstWindowMinutes']}
              tooltip="If the post keeps getting activity, how long we wait before the second notification about it."
              rules={[
                { required: true, message: 'Required' },
                notShorterThan('firstWindowMinutes'),
              ]}
            >
              <InputNumber
                min={1}
                max={1440}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="3rd notification after (min)"
              name="thirdWindowMinutes"
              dependencies={['secondWindowMinutes']}
              tooltip="How long we wait before the third notification about the same post."
              rules={[
                { required: true, message: 'Required' },
                notShorterThan('secondWindowMinutes'),
              ]}
            >
              <InputNumber
                min={1}
                max={1440}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="After that, every (min)"
              name="busyWindowMinutes"
              dependencies={['thirdWindowMinutes']}
              tooltip="For a post that stays busy, we send at most one notification this often."
              rules={[
                { required: true, message: 'Required' },
                notShorterThan('thirdWindowMinutes'),
              ]}
            >
              <InputNumber
                min={1}
                max={1440}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldsValue }) => {
            const waits = describeWaits(getFieldsValue());
            return waits ? (
              <Alert
                type="info"
                showIcon
                message={`Example for one busy post: ${waits}`}
                style={{ marginBottom: 24 }}
              />
            ) : null;
          }}
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Start over after a quiet post (hours)"
              name="quietResetHours"
              tooltip="If a post gets no notification for this many hours, the next like or comment is treated like the first one again and arrives after the 1st wait."
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                min={1}
                max={168}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label='Count people in "Ana and 4 others" from the last (hours)'
              name="countPeopleHours"
              tooltip="For comments and replies, how far back we count people. Likes always count everyone who likes the post."
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                min={1}
                max={168}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Push limit per person</Divider>
        <Paragraph type="secondary">
          Across all posts together. Over the limit the notification still shows
          up in the app&apos;s notification list, just without a push on the
          phone. Set 0 to send no pushes at all.
        </Paragraph>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Max pushes per hour"
              name="pushesPerHour"
              dependencies={['pushesPerDay']}
              tooltip="The most phone pushes one person gets in an hour from likes, comments and replies."
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    value === undefined ||
                    value === null ||
                    value <= getFieldValue('pushesPerDay')
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Cannot be more than the daily limit.')
                        ),
                }),
              ]}
            >
              <InputNumber
                min={0}
                max={60}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Max pushes per day"
              name="pushesPerDay"
              tooltip="The most phone pushes one person gets in a day (UTC) from likes, comments and replies."
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                min={0}
                max={500}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Text type="secondary">
          Changes apply to the next notification that opens; ones already
          waiting keep their current timing.
        </Text>
        <Form.Item style={{ marginTop: 16 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!!error}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default EngagementNotificationSettings;
